/**
 * ShippingBillingAddressStep
 *
 * Reusable component for collecting shipping and billing addresses
 * in intake forms. Includes a "same as shipping" toggle for billing.
 * Used across Men's, Women's, Weight Loss, and Peptide intake flows.
 */

import React, { useEffect } from 'react'
import { Package, CreditCard, ChevronDown } from 'lucide-react'
import AddressAutocomplete from '@/components/intake/weightloss/AddressAutocomplete'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AddressFields {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
}

export interface ShippingBillingAddressData {
  shippingAddress: AddressFields
  billingAddress: AddressFields
  billingSameAsShipping: boolean
}

export const initialAddressFields: AddressFields = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
}

export const initialShippingBillingData: ShippingBillingAddressData = {
  shippingAddress: { ...initialAddressFields },
  billingAddress: { ...initialAddressFields },
  billingSameAsShipping: true,
}

interface ShippingBillingAddressStepProps {
  shippingAddress: AddressFields
  billingAddress: AddressFields
  billingSameAsShipping: boolean
  onShippingChange: (field: keyof AddressFields, value: string) => void
  onBillingChange: (field: keyof AddressFields, value: string) => void
  onBillingSameToggle: (value: boolean) => void
  /** Optional dark theme variant (matches intake form styling) */
  variant?: 'dark' | 'light'
}

// ── US States ─────────────────────────────────────────────────────────────────

const US_STATES = [
  { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' },
  { abbr: 'AZ', name: 'Arizona' }, { abbr: 'AR', name: 'Arkansas' },
  { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' },
  { abbr: 'FL', name: 'Florida' }, { abbr: 'GA', name: 'Georgia' },
  { abbr: 'HI', name: 'Hawaii' }, { abbr: 'ID', name: 'Idaho' },
  { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' },
  { abbr: 'IA', name: 'Iowa' }, { abbr: 'KS', name: 'Kansas' },
  { abbr: 'KY', name: 'Kentucky' }, { abbr: 'LA', name: 'Louisiana' },
  { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' },
  { abbr: 'MA', name: 'Massachusetts' }, { abbr: 'MI', name: 'Michigan' },
  { abbr: 'MN', name: 'Minnesota' }, { abbr: 'MS', name: 'Mississippi' },
  { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' },
  { abbr: 'NE', name: 'Nebraska' }, { abbr: 'NV', name: 'Nevada' },
  { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
  { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' },
  { abbr: 'NC', name: 'North Carolina' }, { abbr: 'ND', name: 'North Dakota' },
  { abbr: 'OH', name: 'Ohio' }, { abbr: 'OK', name: 'Oklahoma' },
  { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' },
  { abbr: 'RI', name: 'Rhode Island' }, { abbr: 'SC', name: 'South Carolina' },
  { abbr: 'SD', name: 'South Dakota' }, { abbr: 'TN', name: 'Tennessee' },
  { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' },
  { abbr: 'VT', name: 'Vermont' }, { abbr: 'VA', name: 'Virginia' },
  { abbr: 'WA', name: 'Washington' }, { abbr: 'WV', name: 'West Virginia' },
  { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' },
  { abbr: 'DC', name: 'District of Columbia' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

export function isAddressComplete(addr: AddressFields): boolean {
  return Boolean(
    addr.addressLine1?.trim() &&
    addr.city?.trim() &&
    addr.state?.trim() &&
    addr.postalCode?.trim() &&
    /^\d{5}(-\d{4})?$/.test(addr.postalCode.trim())
  )
}

export function isShippingBillingComplete(data: ShippingBillingAddressData): boolean {
  if (!isAddressComplete(data.billingAddress)) return false
  if (!data.billingSameAsShipping && !isAddressComplete(data.shippingAddress)) return false
  return true
}

// ── Address Form Block ────────────────────────────────────────────────────────

const AddressBlock: React.FC<{
  address: AddressFields
  onChange: (field: keyof AddressFields, value: string) => void
  prefix: string
  variant: 'dark' | 'light'
}> = ({ address, onChange, prefix, variant }) => {
  const isDark = variant === 'dark'

  const inputCls = isDark
    ? 'w-full px-4 py-3 rounded-lg text-sm text-white placeholder:text-white/30 bg-white/5 border border-white/10 focus:border-[#4A8A9A] focus:ring-1 focus:ring-[#4A8A9A] outline-none transition-all'
    : 'w-full px-4 py-3 rounded-lg text-sm border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all'

  const labelCls = isDark
    ? 'block text-sm font-medium text-white/70 mb-1.5'
    : 'block text-sm font-medium text-gray-700 mb-1.5'

  const selectCls = isDark
    ? 'w-full px-4 py-3 pr-10 rounded-lg text-sm text-white bg-white/5 border border-white/10 focus:border-[#4A8A9A] focus:ring-1 focus:ring-[#4A8A9A] outline-none transition-all appearance-none'
    : 'w-full px-4 py-3 pr-10 rounded-lg text-sm border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all appearance-none'

  // Dropdown options inherit color from the <select>, but the popup background is
  // browser/OS-controlled. On a transparent dark <select>, options end up white-on-white
  // in the popup. Force explicit colors on each <option> so they stay readable.
  const optionStyle = isDark
    ? { backgroundColor: '#0F172A', color: '#E6E9F0' }
    : { backgroundColor: '#FFFFFF', color: '#1E293B' }

  return (
    <div className="space-y-4">
      {/* Address Line 1 — with Google Places Autocomplete */}
      <div>
        <label htmlFor={`${prefix}-line1`} className={labelCls}>
          Street Address <span className="text-red-400">*</span>
        </label>
        <AddressAutocomplete
          value={address.addressLine1}
          onChange={(value) => onChange('addressLine1', value)}
          onAddressSelect={(components) => {
            // Auto-fill all address fields from Google Places selection
            if (components.addressLine1) onChange('addressLine1', components.addressLine1)
            if (components.city) onChange('city', components.city)
            if (components.state) onChange('state', components.state)
            if (components.postalCode) onChange('postalCode', components.postalCode)
          }}
          placeholder="Start typing your address..."
        />
      </div>

      {/* Address Line 2 */}
      <div>
        <label htmlFor={`${prefix}-line2`} className={labelCls}>
          Apt / Suite / Unit
        </label>
        <input
          id={`${prefix}-line2`}
          type="text"
          value={address.addressLine2}
          onChange={(e) => onChange('addressLine2', e.target.value)}
          placeholder="Apt 4B"
          className={inputCls}
        />
      </div>

      {/* City / State / Zip Row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="col-span-2 sm:col-span-1">
          <label htmlFor={`${prefix}-city`} className={labelCls}>
            City <span className="text-red-400">*</span>
          </label>
          <input
            id={`${prefix}-city`}
            type="text"
            value={address.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="New York"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-state`} className={labelCls}>
            State <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <select
              id={`${prefix}-state`}
              value={address.state}
              onChange={(e) => onChange('state', e.target.value)}
              className={selectCls}
            >
              <option value="" style={optionStyle}>Select</option>
              {US_STATES.map((s) => (
                <option key={s.abbr} value={s.abbr} style={optionStyle}>
                  {s.abbr} — {s.name}
                </option>
              ))}
            </select>
            <ChevronDown
              className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 ${
                isDark ? 'text-white/40' : 'text-slate-400'
              }`}
            />
          </div>
        </div>
        <div>
          <label htmlFor={`${prefix}-zip`} className={labelCls}>
            ZIP Code <span className="text-red-400">*</span>
          </label>
          <input
            id={`${prefix}-zip`}
            type="text"
            value={address.postalCode}
            onChange={(e) => onChange('postalCode', e.target.value)}
            placeholder="10001"
            maxLength={10}
            className={inputCls}
          />
        </div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

const ShippingBillingAddressStep: React.FC<ShippingBillingAddressStepProps> = ({
  shippingAddress,
  billingAddress,
  billingSameAsShipping,
  onShippingChange,
  onBillingChange,
  onBillingSameToggle,
  variant = 'dark',
}) => {
  const isDark = variant === 'dark'

  // When toggling "same as billing" ON, copy billing → shipping
  useEffect(() => {
    if (billingSameAsShipping) {
      Object.keys(billingAddress).forEach((key) => {
        const k = key as keyof AddressFields
        if (shippingAddress[k] !== billingAddress[k]) {
          onShippingChange(k, billingAddress[k])
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingSameAsShipping])

  const sectionBg = isDark
    ? 'bg-white/[0.02] border border-white/10 rounded-xl p-6'
    : 'bg-gray-50 border border-gray-200 rounded-xl p-6'

  const headingCls = isDark ? 'text-white' : 'text-gray-900'
  const subCls = isDark ? 'text-white/50' : 'text-gray-500'
  const iconCls = isDark ? 'text-[#4A8A9A]' : 'text-blue-600'

  const toggleBg = billingSameAsShipping
    ? 'bg-[#4A8A9A]'
    : isDark
      ? 'bg-white/20'
      : 'bg-gray-300'

  return (
    <div>
      <h2
        className={`text-2xl font-light mb-2 ${headingCls}`}
        style={isDark ? { color: '#E6E9F0' } : undefined}
      >
        Billing &amp; Shipping{' '}
        <span className={isDark ? 'text-[#4A8A9A] italic' : 'text-blue-600 italic'}>
          Address
        </span>
      </h2>
      <p className={`mb-8 text-sm ${subCls}`}>
        We need your billing address for payment and a shipping address to deliver medications.
      </p>

      {/* ── Billing Address ───────────────────────────────────────────── */}
      <div className={`${sectionBg} mb-6`}>
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className={`h-5 w-5 ${iconCls}`} />
          <h3 className={`font-semibold ${headingCls}`}>Billing Address</h3>
        </div>
        <AddressBlock
          address={billingAddress}
          onChange={onBillingChange}
          prefix="billing"
          variant={variant}
        />
      </div>

      {/* ── Shipping Same As Billing Toggle ────────────────────────────── */}
      <div className={`${sectionBg} mb-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className={`h-5 w-5 ${iconCls}`} />
            <h3 className={`font-semibold ${headingCls}`}>Shipping Address</h3>
          </div>
          <button
            type="button"
            onClick={() => onBillingSameToggle(!billingSameAsShipping)}
            className="flex items-center gap-2 text-sm"
          >
            <span className={subCls}>Same as billing</span>
            <div
              className={`relative w-10 h-5 rounded-full transition-colors ${toggleBg}`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  billingSameAsShipping ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </button>
        </div>

        {/* Shipping fields (visible only when NOT same as billing) */}
        {!billingSameAsShipping && (
          <div className="mt-4">
            <AddressBlock
              address={shippingAddress}
              onChange={onShippingChange}
              prefix="shipping"
              variant={variant}
            />
          </div>
        )}

        {billingSameAsShipping && (
          <p className={`mt-3 text-sm ${subCls}`}>
            Your shipping address will be the same as your billing address.
          </p>
        )}
      </div>
    </div>
  )
}

export default ShippingBillingAddressStep
